<script setup lang="ts">
import type { Ref } from "vue"
import type { IGuestMessage } from "@/types"
import { toTypedSchema } from "@vee-validate/zod"
import { useUrlSearchParams } from "@vueuse/core"
import { useForm } from "vee-validate"
import { computed, ref } from "vue"
import { useRouter } from "vue-router"
import { z } from "zod"
import { useAddGuestMessage, useGetEventVault, useListAlbums } from "@/api"
import { AlbumSelector, AlbumsLayout, Footer, MainGalleryHiddenMessage } from "@/components"
import { AlbumIdEventBus } from "@/components/Nav/AlbumIdEventBus"
import { Alert, Button, Hint, Input, Textarea } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam } from "@/composables"
import { useTranslation } from "@/composables/useTranslation"
import { ValidationException } from "@/http"
import { GuestLayout } from "@/layouts"
import { EventVaultStatus } from "@/models"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { useTemplateStore } from "@/stores/templateStore"

definePage({
  meta: {
    requiresCookies: true,
    pageLayout: "multi",
  },
})

const eventVaultId = useConvertedRouteParam<number>("id", "/guest/[id]/message", value => Number(value))

const templateStore = useTemplateStore()
const eventVaultStore = useEventVaultStore()

const { t } = useTranslation()
const toast = useToast()
const router = useRouter()
const urlParams = useUrlSearchParams<{ album?: number }>("history")
const currentAlbumId: Ref<number | undefined> = ref(urlParams.album)

AlbumIdEventBus.on((id) => {
  currentAlbumId.value = id
})

const infoMessage = ref<string>(t("add_guest_message_page.info_message"))
const emailRequiredMessage = t("add_guest_message_page.error.email")
const validationErrors = ref()

const { data: eventVault, isLoading: isEventLoading } = useGetEventVault(eventVaultId)
const { data: albums, isLoading: isLoadingAlbums } = useListAlbums(eventVaultId)

const validationSchema = toTypedSchema(
  z.object({
    fromName: z.string().optional(),
    fromEmail: eventVault?.value?.eventVaultSettings?.guestEmailRequired
      ? z.string().email(emailRequiredMessage)
      : z.union([z.literal(""), z.string().email(emailRequiredMessage)]),
    message: z
      .string()
      .min(1, t("add_guest_message_page.error.note_min"))
      .max(5000, t("add_guest_message_page.error.note_max")),
  }),
)

const { handleSubmit, handleReset } = useForm({
  validationSchema,
  initialValues: {
    fromName: "",
    fromEmail: "",
    message: "",
  },
})

const { mutate, isPending: isSubmitLoading } = useAddGuestMessage()

const onSubmit = handleSubmit(({ fromName, fromEmail, message }) => {
  if (!eventVaultId.value) {
    toast.error({ title: t("something_went_wrong"), description: t("add_guest_message_page.missing_id") })
    return
  }

  validationErrors.value = null

  const guestMessage: IGuestMessage = { fromName, fromEmail, message, eventAlbumID: currentAlbumId.value }

  mutate({ message: guestMessage, eventVaultId: eventVaultId.value }, {
    onSuccess: (message: string) => {
      const successMessage = message || t("add_guest_message_page.success")

      handleReset()

      router.push(currentAlbumId.value
        ? `/Guest/Welcome/${eventVaultId.value}?album=${currentAlbumId.value}&message=${successMessage}`
        : `/Guest/Welcome/${eventVaultId.value}/?message=${successMessage}`)
    },
    onError: (error: unknown) => {
      let errorMessage = t("try_again")

      if (error instanceof ValidationException) {
        errorMessage = t("validation_error")
        validationErrors.value = error
      }

      toast.error({
        title: t("something_went_wrong"),
        description: errorMessage,
        icon: "Caution",
      })
    },
  })
})

const isGuestbookHidden = computed(() => {
  return eventVault.value?.eventVaultSettings?.hideGuestbook
})

const isFeatureDisabled = computed(() => eventVault.value?.status === EventVaultStatus.Expired)

const isGalleryUnavailable = computed(
  () =>
    eventVault.value?.eventVaultSettings?.hideMainGallery
    && !currentAlbumId.value
    && !isEventLoading.value
    && !eventVaultStore.isHost,
)
</script>

<template>
  <GuestLayout hide-footer>
    <AlbumsLayout>
      <template #sidebar>
        <AlbumSelector
          v-if="eventVault?.eventVaultSettings?.albumsEnabled"
          v-model="currentAlbumId"
          :event-vault-id="eventVaultId"
          :event-cover-image="eventVault?.coverImageUrl"
          :albums="albums"
          :hide-main-gallery="eventVault.eventVaultSettings?.hideMainGallery && !eventVaultStore.isHost"
          :is-loading="isLoadingAlbums"
        />
      </template>
      <MainGalleryHiddenMessage
        v-if="isGalleryUnavailable"
        :page-message="t('main_gallery_hidden_message.add_guest_message_message')"
      />
      <div v-else class="container gpx-reminder">
        <div>
          <h1 class="gpx-message-title heading-subtitle">
            {{ t("add_guest_message_page.title") }}
          </h1>
          <p class="gpx-message-description" :class="{ 'gpx-message-description_bottom-margin': !isGuestbookHidden }">
            {{ t("add_guest_message_page.message") }}
          </p>
          <div v-if="isGuestbookHidden && !isEventLoading" class="flex">
            <Alert v-model="infoMessage" variant="info" class="gpx-message-alert_desktop" prefix-icon="CircleInfo" :closable="false" />
          </div>
          <Hint v-if="isGuestbookHidden && !isEventLoading" class="gpx-message-alert_mobile" :hint="infoMessage" />
        </div>
        <div>
          <Transition name="gpx-fade" mode="out-in">
            <div v-if="isSubmitLoading && isEventLoading">
              <div class="gpx-message-fields">
                <div class="gpx-skeleton gpx-message-input_loading" />
                <div class="gpx-skeleton gpx-message-input_loading" />
                <div class="gpx-skeleton gpx-message-textarea_loading" />
                <div class="gpx-skeleton gpx-message-button_loading" />
              </div>
            </div>
            <form v-else @submit.prevent="onSubmit">
              <div class="gpx-message-fields">
                <Input
                  name="fromName" :label="t('add_guest_message_page.form.name')"
                  :hint="t('add_guest_message_page.form.name_hint')"
                  :disabled="isFeatureDisabled"
                />
                <Input
                  name="fromEmail" :label="t('add_guest_message_page.form.email')"
                  :error="validationErrors?.firstValidationError('Email')"
                  :disabled="isFeatureDisabled"
                />
                <Textarea name="message" :label="t('add_guest_message_page.form.note')" :disabled="isFeatureDisabled" />
                <div>
                  <Button size="lg" data-test="add-message-submit" :disabled="isFeatureDisabled">
                    {{
                      t("add_guest_message_page.form.leave_note_button")
                    }}
                  </Button>
                </div>
              </div>
            </form>
          </Transition>
        </div>
      </div>
      <Footer
        :custom-logo="templateStore.customFooterImage"
        :custom-footer-links="templateStore.customFooterLinks"
        :is-freemium-event="eventVault?.freemiumMode"
      />
    </AlbumsLayout>
  </GuestLayout>
</template>

<style lang="postcss" scoped>
@reference "../../../assets/styles.css";

.gpx-reminder {
  padding-top: 1rem;
  padding-bottom: 3rem;
}

.gpx-message-title {
  margin: 1rem 0;

  @apply font-bold;
}

.gpx-message-description {
  padding: 0 0 0.5rem;

  @apply text-gray-700;
}

.gpx-message-description_bottom-margin {
  margin-bottom: 2rem;
}

.gpx-message-alert_desktop {
  display: none;
}

.gpx-message-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.gpx-message-input_loading {
  height: 3.5rem;
}

.gpx-message-textarea_loading {
  height: 7rem;
}

.gpx-message-button_loading {
  width: 12rem;
  height: 3rem;
}

@media (width >=768px) {
  .gpx-reminder {
    padding-top: 5rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  .gpx-message-title {
    margin: 0 0 1.5rem;
  }

  .gpx-message-description {
    margin-bottom: 1rem;
  }

  .gpx-message-alert_mobile {
    display: none;
  }

  .gpx-message-alert_desktop {
    display: flex;
    flex-shrink: 1;
    flex-grow: 0;
  }
}
</style>
